import { metrics } from '@atlassian/browser-metrics';
import { ff } from '@atlassian/jira-feature-flagging';
import { isInSample } from '@atlassian/jira-issue-sample-utils';
import type {
	Agile,
	TransformedDataToLegacyGira,
	TransformedDataToLegacyGraphql,
} from '@atlassian/jira-issue-shared-types';
import {
	getIsConsistencyCheckEnabled,
	getIsNewValueReturned,
} from '@atlassian/jira-platform-rollout-service-async';
import type { ProjectKey } from '@atlassian/jira-shared-types';
import type { AggResponseData } from './common/types';
import {
	getStatusesDeprecatedValue,
	transformAggFieldsToLegacyFields,
	transformAggFieldsToLegacyParentFields,
	transformAggIssueToHierarchyLegacyFields,
	transformAggProjectToProjectContext,
	transformAggResponseToIssueLinks,
	transformAggResponseToLegacyChildIssues,
	transformServerChildIssueFieldsToAssociatedIssues,
	transformServerParentFieldsToParentIssue,
	transformAggResponseToTimeTrackingConfig,
	transformAggResponseToIssueLinkTypes,
	transformAggResponseSubtasksConfig,
	transformAggIsArchivedToLegacyGira,
	transformAggUserPreferencesToLegacyGira,
	transformAggUserPreferencesToLegacyGiraActivityFeedSortOrder,
	transformAggAttachmentsToLegacyGira,
} from './common/utils';

const GIRA_AGG_MIGRATION_ISARCHIVED_FLAG = 'issue.details.gira-agg-migration-isarchived';

const TIME_TO_TRANSFORM_AGG = 'issue-agg-field-transformers';
const timeToTransformAgg = metrics.custom({
	key: TIME_TO_TRANSFORM_AGG,
});

export const ISSUE_FIELDS_NOT_AVAILABLE = 'Issue fields are not available';

const transformAgile = (data: AggResponseData): Agile => {
	// epicLinkFieldId is a temporary solution until GIC supports the unified parent creation.
	const epicKey = data.jira?.epicLinkFieldKey || '';

	return {
		epic: { key: epicKey },
	};
};

// This function transforms AGG issue-view data to the legacy GraphQL format so we can feed it to the Redux store
// @ExportForTesting
export const transformAggToLegacyGraphql = (
	data: AggResponseData,
): TransformedDataToLegacyGraphql => {
	const issue = data.jira.issueByKey;

	if (!issue?.fields) {
		throw new Error(ISSUE_FIELDS_NOT_AVAILABLE);
	}

	const fieldsEdges = issue?.fields?.edges;

	const project = transformAggProjectToProjectContext(fieldsEdges);
	const isNextGenProject = project.isSimplified === true;

	const hierarchyLevelFields = transformAggIssueToHierarchyLegacyFields(issue);

	const fields = transformAggFieldsToLegacyFields(fieldsEdges);
	const parentFields = transformAggFieldsToLegacyParentFields(fieldsEdges);

	const { fieldsState: childrenIssueFields, childIssueMeta } =
		transformAggResponseToLegacyChildIssues(data, {
			issueHierarchyLevel: fields.issuetype?.value.hierarchyLevel ?? 0,
			isNextGenProject,
		});

	const { childrenIssues, subtasks } = transformServerChildIssueFieldsToAssociatedIssues(
		childrenIssueFields,
		{
			isTeamManagedProject: isNextGenProject,
			childIssueMeta,
		},
	);

	const agile = transformAgile(data);

	const issueId = issue?.issueId || '';
	const viewScreenId = issue?.screenId;
	const viewScreen = viewScreenId === null ? null : { viewScreenId };

	return {
		childIssuesLimit: data.jira.childIssuesLimit,
		project,
		issue: {
			fields: {
				...fields,
				...childrenIssueFields,
				...hierarchyLevelFields,
				...parentFields,
			},
			id: issueId,
			agile,
			tabs: {}, // Tabs information is coming from containersByType API in Gira instead
			...viewScreen,
		},
		createdDate: fields.created?.value || null,
		updatedDate: fields.updated?.value || null,
		statuses: getStatusesDeprecatedValue(fields),
		issueLinks: transformAggResponseToIssueLinks(data),
		subtasks,
		childrenIssues,
		parentIssue: transformServerParentFieldsToParentIssue(parentFields),
	};
};

export const transformAggResponseToLegacyGraphql = (data: AggResponseData) => {
	if (data === undefined || data === null) return null;

	try {
		if (isInSample()) {
			timeToTransformAgg.start();
		}
		const legacyGraphqlFields = transformAggToLegacyGraphql(data);
		timeToTransformAgg.stop();

		return legacyGraphqlFields;
	} catch (error: unknown) {
		timeToTransformAgg.cancel();
		throw error;
	}
};

const transformAggJiraSettingsToLegacyGira = (aggData: AggResponseData) => ({
	subtasksConfiguration: transformAggResponseSubtasksConfig(aggData),
	timeTrackingConfiguration: transformAggResponseToTimeTrackingConfig(aggData),
	issueLinkTypes: transformAggResponseToIssueLinkTypes(aggData),
});

const TIME_TO_TRANSFORM_AGG_TO_GIRA = 'issue-agg-to-gira-transform';

const timeToTransformAggToLegacyGira = metrics.custom({
	key: TIME_TO_TRANSFORM_AGG_TO_GIRA,
});

// This function transforms AGG issue-view data to the legacy Gira format so we can feed it to the Redux store
// @ExportForTesting
export const transformAggResponseToLegacyGira = (
	aggData: AggResponseData,
	projectKey: ProjectKey,
	isConsistencyCheck = false,
): TransformedDataToLegacyGira => {
	// If this transform is for the consistency check, we include slices if they have vaue 'CHECK' or 'CHECK_RETURN_NEW'
	// If this transform is for the issue view, we include slices if they have vaue 'NEW' or 'CHECK_RETURN_NEW'
	const shouldIncludeSlice = isConsistencyCheck
		? getIsConsistencyCheckEnabled
		: getIsNewValueReturned;

	const isNewValueIsArchived = shouldIncludeSlice(GIRA_AGG_MIGRATION_ISARCHIVED_FLAG);

	try {
		if (isInSample()) {
			timeToTransformAggToLegacyGira.start();
		}

		const transformedData = {
			jiraSettings: transformAggJiraSettingsToLegacyGira(aggData),
			...(isNewValueIsArchived
				? {
						isArchived: transformAggIsArchivedToLegacyGira(aggData),
					}
				: {}),
			myPreferences: transformAggUserPreferencesToLegacyGira(aggData, projectKey),
			activitySortOrder: transformAggUserPreferencesToLegacyGiraActivityFeedSortOrder(aggData),
			...(ff('issue.details.gira-agg-migration-attachments_ecebe')
				? {
						attachments: transformAggAttachmentsToLegacyGira(aggData),
					}
				: {}),
		};

		const issueId = aggData?.jira.issueByKey?.issueId;
		timeToTransformAggToLegacyGira.stop({
			customData: {
				issueId: issueId || '',
				[GIRA_AGG_MIGRATION_ISARCHIVED_FLAG]: isNewValueIsArchived,
			},
		});

		return transformedData;
	} catch (error: unknown) {
		timeToTransformAgg.cancel();
		throw error;
	}
};
